import React, { useId, useState } from 'react';
import Button from '@hero/ui-kit/inputs/Button';
import { Input, Props as InputProps } from '@hero/ui-kit/inputs/Input';
import { DefaultSelect, Props as SelectProps } from '@hero/ui-kit/inputs/DefaultSelect';
import { TextArea, Props as TextAreaProps } from '@hero/ui-kit/inputs/TextArea';
import AccessiblyHiddenChoice from '@hero/ui-kit/inputs/AccessiblyHiddenChoice';
import CheckboxSquare from '@hero/ui-kit/graphics/CheckboxSquare';
import AlertIcon from '@hero/ui-kit/icons/utility/AlertIcon';
import RadioCircle from '@hero/ui-kit/graphics/RadioCircle';
import useTheme from '@hero/branding/theme';
import * as layout from './markLayout.module.css';
import H from '@hero/ui-kit/typography/H';
import Label from '@hero/ui-kit/typography/Label';
import withFocus, { FocusProps } from '@hero/ui-kit/hocs/withFocus';

// Mark owns controlled drafts. Use the package controls without their react-hook-form
// registration wrappers, which would introduce a second owner of each field's value.
const ControlledInput = withFocus<Omit<InputProps, keyof FocusProps>, HTMLInputElement>(Input);
const ControlledSelect = withFocus<Omit<SelectProps, keyof FocusProps>, HTMLSelectElement>(
    DefaultSelect
);
const ControlledTextArea = withFocus<Omit<TextAreaProps, keyof FocusProps>, HTMLTextAreaElement>(
    TextArea
);

const layoutStyles = (styles?: React.CSSProperties): React.CSSProperties => {
    const layout: React.CSSProperties = {};
    const keys = [
        'width',
        'maxWidth',
        'minWidth',
        'margin',
        'marginTop',
        'marginBottom',
        'marginLeft',
        'marginRight',
        'flex',
        'flexShrink',
        'alignSelf',
        'whiteSpace',
        'textAlign',
        'display',
        'gap',
        'alignItems'
    ] as const;
    keys.forEach((key) => {
        if (styles?.[key] != null) Object.assign(layout, { [key]: styles[key] });
    });
    return layout;
};

const assignRef = <T,>(ref: React.ForwardedRef<T>, value: T | null) => {
    if (typeof ref === 'function') ref(value);
    else if (ref) ref.current = value;
};

// The package Button doesn't forward native attributes. Retain the attributes
// used by Mark's tab lists, menus and validation focus targets on its actual button.
const setAttributes = (element: HTMLElement, attributes: Record<string, unknown>) => {
    Object.entries(attributes).forEach(([name, value]) => {
        if (
            name.startsWith('aria-') ||
            name.startsWith('data-') ||
            ['id', 'title', 'role', 'name', 'tabIndex', 'className'].includes(name)
        ) {
            if (value == null) element.removeAttribute(name);
            else
                element.setAttribute(
                    name === 'tabIndex' ? 'tabindex' : name === 'className' ? 'class' : name,
                    String(value)
                );
        }
    });
};

const forwardButtonEvent = <E extends React.SyntheticEvent<HTMLButtonElement>>(
    event: React.SyntheticEvent<HTMLSpanElement>,
    handler?: (event: E) => void
) => {
    const wrapper = event.currentTarget;
    const button = wrapper.querySelector('button');
    if (!button || !handler) return;
    try {
        Object.assign(event, { currentTarget: button });
        handler(event as unknown as E);
    } finally {
        event.currentTarget = wrapper;
    }
};

export const MarkButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement> & {
        variant?: React.ComponentProps<typeof Button>['variant'];
        size?: React.ComponentProps<typeof Button>['size'];
        tone?: 'danger';
        shape?: 'rounded';
        compact?: boolean;
    }
>(
    (
        {
            children,
            onClick,
            onMouseDown,
            onKeyDown,
            onFocus,
            onBlur,
            type = 'button',
            disabled,
            className,
            style,
            variant,
            size = 'regular',
            tone,
            shape,
            compact = false,
            ...attributes
        },
        ref
    ) => {
        const { borders } = useTheme();
        return (
            <span
                style={{ display: 'contents' }}
                onKeyDown={(event) => forwardButtonEvent(event, onKeyDown)}
                onMouseDown={(event) => forwardButtonEvent(event, onMouseDown)}
                onFocus={(event) => forwardButtonEvent(event, onFocus)}
                onBlur={(event) => forwardButtonEvent(event, onBlur)}
                ref={(element) => {
                    const button = element?.querySelector('button') ?? null;
                    if (button) setAttributes(button, attributes);
                    assignRef(ref, button);
                }}
            >
                <Button
                    type={type}
                    disabled={disabled}
                    className={className}
                    onClick={onClick}
                    size={compact ? 'small' : size}
                    variant={
                        variant ??
                        (style?.backgroundColor === '#f26c3a' ||
                        attributes['aria-selected'] === true ||
                        attributes['aria-pressed'] === true ||
                        attributes['aria-current'] === 'step'
                            ? 'primary'
                            : 'outlineSecondary')
                    }
                    noDefaultMargin
                    styles={{
                        minHeight: compact ? '3.6rem' : '4.4rem',
                        ...layoutStyles(style),
                        ...(shape === 'rounded' && borders.shape.rounded),
                        ...(tone === 'danger' &&
                            !disabled && {
                                color: 'var(--mark-danger)',
                                borderColor: 'var(--mark-danger)'
                            })
                    }}
                >
                    {children}
                </Button>
            </span>
        );
    }
);

export const MarkConfirmActions: React.FC<{
    confirmLabel: string;
    cancelLabel: string;
    onConfirm: () => void;
    onCancel: () => void;
    disabled?: boolean;
    cancelDisabled?: boolean;
}> = ({ confirmLabel, cancelLabel, onConfirm, onCancel, disabled, cancelDisabled = false }) => (
    <div className={layout.confirmActions}>
        <MarkButton tone="danger" onClick={onConfirm} disabled={disabled}>
            {confirmLabel}
        </MarkButton>
        <MarkButton variant="outlineSecondary" onClick={onCancel} disabled={cancelDisabled}>
            {cancelLabel}
        </MarkButton>
    </div>
);

export const MarkInput = React.forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement> & { compact?: boolean }
>(
    (
        {
            id,
            name,
            style,
            type,
            size: _size,
            compact = false,
            value,
            spellCheck,
            min,
            step,
            onChange,
            onFocus,
            onBlur,
            ...props
        },
        ref
    ) => {
        const generatedId = useId();
        const fieldName = name ?? id ?? generatedId;
        const { inputs, borders } = useTheme();
        const isError = props['aria-invalid'] === true || props['aria-invalid'] === 'true';
        return (
            <div className={layout.inputWrap}>
                <ControlledInput
                    {...props}
                    // The kit's icon sits at a fixed top that misses shorter inputs; draw a centered one here.
                    isError={false}
                    ref={ref}
                    name={fieldName}
                    id={id ?? fieldName}
                    type={type as InputProps['type']}
                    value={value as string | number | undefined}
                    spellCheck={
                        spellCheck == null
                            ? undefined
                            : spellCheck === true || spellCheck === 'true'
                    }
                    {...({ min, step } as Pick<InputProps, 'min' | 'step'>)}
                    onFocusCapture={onFocus}
                    styles={{
                        width: '100%',
                        minWidth: 0,
                        margin: 0,
                        ...(compact && { ...inputs.size.small, minHeight: '3.6rem' }),
                        ...(isError && {
                            ...borders.error,
                            paddingRight: `calc(${inputs.base.paddingRight} * 2.5)`
                        }),
                        ...layoutStyles(style)
                    }}
                    wrapperStyles={{ width: '100%' }}
                    onChange={async (event) => {
                        onChange?.(event as React.ChangeEvent<HTMLInputElement>);
                    }}
                    onBlur={async (event) => {
                        onBlur?.(event as React.FocusEvent<HTMLInputElement>);
                    }}
                />
                {isError && (
                    <AlertIcon
                        size="utility"
                        aria-hidden
                        styles={{
                            ...inputs.utilityIconStyles,
                            top: '50%',
                            transform: 'translateY(-50%)'
                        }}
                    />
                )}
            </div>
        );
    }
);

export const MarkTimeInput = React.forwardRef<
    HTMLInputElement,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>
>((props, ref) => (
    <MarkInput
        {...props}
        ref={ref}
        type="text"
        placeholder="HH:MM"
        pattern="([01][0-9]|2[0-3]):[0-5][0-9]"
    />
));

// H renders React nodes but its public children type is narrower. It also
// omits native attributes needed by dialog headings and wizard focus targets.
const PackageHeading = H as React.ComponentType<
    Omit<React.ComponentProps<typeof H>, 'children'> & { children: React.ReactNode }
>;
type HeadingProps = Omit<React.HTMLAttributes<HTMLHeadingElement>, 'role'> & {
    role: React.ComponentProps<typeof H>['role'];
};
export const MarkHeading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
    ({ role, children, style, ...attributes }, ref) => (
        <span
            style={{ display: 'contents' }}
            ref={(element) => {
                const heading = element?.querySelector<HTMLHeadingElement>(role ?? 'h4') ?? null;
                if (heading) setAttributes(heading, attributes);
                assignRef(ref, heading);
            }}
        >
            <PackageHeading role={role} noDefaultMargin styles={layoutStyles(style)}>
                {children}
            </PackageHeading>
        </span>
    )
);

const PackageLabel = Label as React.ComponentType<
    Omit<React.ComponentProps<typeof Label>, 'children'> & { children: React.ReactNode }
>;
export const MarkLabel: React.FC<
    React.LabelHTMLAttributes<HTMLLabelElement> & { choice?: boolean; compact?: boolean }
> = ({ children, htmlFor, style, choice = false, compact = false, ...attributes }) => {
    const { typography } = useTheme();
    return (
        <span
            style={{ display: 'contents' }}
            ref={(element) => {
                const label = element?.querySelector('label');
                if (label) {
                    setAttributes(label, attributes);
                    if (!htmlFor)
                        label.htmlFor = label.querySelector('input, select, textarea')?.id ?? '';
                }
            }}
        >
            <PackageLabel
                htmlFor={htmlFor ?? ''}
                styles={
                    choice
                        ? {
                              ...typography.paragraph[compact ? 'small' : 'regular'],
                              textTransform: 'none',
                              letterSpacing: 0,
                              ...layoutStyles(style),
                              display: 'flex',
                              alignItems: 'center',
                              gap: compact ? '0.6rem' : '1.2rem',
                              padding: compact ? '0.4rem 1rem' : '1.6rem',
                              margin: 0,
                              marginTop: 0,
                              marginBottom: 0,
                              marginLeft: 0,
                              marginRight: 0
                          }
                        : layoutStyles(style)
                }
            >
                {children}
            </PackageLabel>
        </span>
    );
};

export const MarkChoiceLabel: React.FC<
    React.LabelHTMLAttributes<HTMLLabelElement> & { compact?: boolean }
> = ({ className, ...props }) => (
    <MarkLabel {...props} choice className={[layout.choice, className].filter(Boolean).join(' ')} />
);

const optionText = (children: React.ReactNode): string =>
    React.Children.toArray(children)
        .map((child) =>
            React.isValidElement<{ children?: React.ReactNode }>(child)
                ? optionText(child.props.children)
                : String(child)
        )
        .join('');

type SelectOption = { value: string | number; label: string; disabled?: boolean };
const selectOptions = (children: React.ReactNode, group = ''): SelectOption[] => {
    const options: SelectOption[] = [];
    React.Children.forEach(children, (child) => {
        if (
            !React.isValidElement<
                React.OptionHTMLAttributes<HTMLOptionElement> & { label?: string }
            >(child)
        )
            return;
        if (child.type === 'option') {
            const label = child.props.label ?? optionText(child.props.children);
            options.push({
                value: String(child.props.value ?? label),
                label: group ? `${group}: ${label}` : label,
                disabled: child.props.disabled
            });
        } else {
            options.push(
                ...selectOptions(
                    child.props.children,
                    child.type === 'optgroup' ? child.props.label : group
                )
            );
        }
    });
    return options;
};

export const MarkSelect = React.forwardRef<
    HTMLSelectElement,
    React.SelectHTMLAttributes<HTMLSelectElement>
>(
    (
        {
            id,
            name,
            children,
            style,
            size: _size,
            onChange,
            onFocus,
            onBlur,
            value,
            defaultValue,
            ...props
        },
        ref
    ) => {
        const generatedId = useId();
        const fieldName = name ?? id ?? generatedId;
        const options = selectOptions(children);
        return (
            <ControlledSelect
                {...props}
                name={fieldName}
                id={id ?? fieldName}
                value={value as string | number | undefined}
                defaultValue={defaultValue as string | number | undefined}
                options={options}
                onFocusCapture={onFocus}
                styles={{ width: '100%', minWidth: 0, margin: 0, ...layoutStyles(style) }}
                wrapperStyles={{ width: '100%' }}
                ref={(element) => {
                    if (element) {
                        setAttributes(element, props);
                        Array.from(element.options).forEach((option, index) => {
                            option.disabled = !!options[index]?.disabled;
                        });
                    }
                    assignRef(ref, element);
                }}
                onChange={async (event) => {
                    onChange?.(event as React.ChangeEvent<HTMLSelectElement>);
                }}
                onBlur={async (event) => {
                    onBlur?.(event as React.FocusEvent<HTMLSelectElement>);
                }}
            />
        );
    }
);

export const MarkTextArea = React.forwardRef<
    HTMLTextAreaElement,
    React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ id, name, style, value, onChange, onFocus, onBlur, ...props }, ref) => {
    const generatedId = useId();
    const fieldName = name ?? id ?? generatedId;
    return (
        <ControlledTextArea
            {...props}
            ref={ref}
            name={fieldName}
            id={id ?? fieldName}
            displayName=""
            value={value as string | number | undefined}
            onFocusCapture={onFocus}
            styles={{ width: '100%', minWidth: 0, margin: 0, ...layoutStyles(style) }}
            onChange={async (event) => {
                onChange?.(event as React.ChangeEvent<HTMLTextAreaElement>);
            }}
            onBlur={async (event) => {
                onBlur?.(event as React.FocusEvent<HTMLTextAreaElement>);
            }}
        />
    );
});

type ChoiceProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    type: 'checkbox' | 'radio';
};
export const MarkChoice = React.forwardRef<HTMLInputElement, ChoiceProps>(
    (
        {
            type,
            id,
            name,
            value,
            checked = false,
            disabled = false,
            onChange,
            onFocus,
            onBlur,
            style,
            ...attributes
        },
        ref
    ) => {
        const generatedId = useId();
        const [isFocused, setFocused] = useState(false);
        const [isHovered, setHovered] = useState(false);
        return (
            <span
                style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    ...style
                }}
            >
                <AccessiblyHiddenChoice
                    type={type}
                    id={id ?? generatedId}
                    name={name ?? id ?? generatedId}
                    value={value == null ? undefined : String(value)}
                    disabled={disabled}
                    isHovered={isHovered}
                    handleMouseEnter={() => setHovered(true)}
                    handleMouseLeave={() => setHovered(false)}
                    handleBlur={() => setFocused(false)}
                    onFocus={(event) => {
                        setFocused(true);
                        onFocus?.(event as React.FocusEvent<HTMLInputElement>);
                    }}
                    onBlur={(event) => {
                        setFocused(false);
                        onBlur?.(event as React.FocusEvent<HTMLInputElement>);
                    }}
                    onChange={(event) => onChange?.(event as React.ChangeEvent<HTMLInputElement>)}
                    ref={(element: HTMLInputElement | null) => {
                        if (element) {
                            element.checked = checked;
                            setAttributes(element, attributes);
                        }
                        assignRef(ref, element);
                    }}
                />
                {type === 'checkbox' ? (
                    <CheckboxSquare
                        isChecked={checked}
                        isHovered={isHovered}
                        isFocused={isFocused}
                        disabled={disabled}
                        styles={{ margin: 0 }}
                    />
                ) : (
                    <RadioCircle
                        status="regular"
                        isSelected={checked}
                        isHovered={isHovered}
                        isFocused={isFocused}
                        disabled={disabled}
                        styles={{ margin: 0, width: '2rem', height: '2rem', minWidth: '2rem' }}
                    />
                )}
            </span>
        );
    }
);
